import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdSearch } from 'react-icons/md';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import Header from 'components/Header';
import Table from 'components/Table';

import './index.css';

const TOTAL_SECTIONS = 7;
const ADMIN_ROLES = new Set(['Admin', 'Super Admin', 'Staff']);
const FILLAPP_ENABLED = 'FillApp Enabled';

type RosterRow = {
    studentOrganizationId: number;
    studentName: string;
    studentEmail: string;
    organizationId: number;
    organizationName: string;
    fillApp: {
        id: number;
        isEnabled: boolean;
        cycleYear: number;
        lastSavedAt: string | null;
        sectionStatus: Record<string, string>;
        progressPercent: number;
    } | null;
};

function relativeTime(iso: string | null): string {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
}

function progressPct(fillApp: RosterRow['fillApp']): number {
    return fillApp?.progressPercent ?? 0;
}

function progressColor(pct: number): string {
    if (pct >= 90) return 'green';
    if (pct < 25) return 'amber';
    return '';
}

function FillApp() {
    const { user } = useAuthContext();
    const navigate = useNavigate();

    const hasFillApp = (user?.customizations || []).includes(FILLAPP_ENABLED);
    const isAdmin = user?.role && ADMIN_ROLES.has(user.role);

    // The roster endpoint is keyed by urlSlug, not by organization, and one slug
    // maps to many orgs — it already returns every org behind the slug that this
    // tutor can see. Requesting once per organization asked the same question
    // twice and listed every student once per membership, so ask once per slug.
    const slugs = useMemo(
        () => Array.from(new Set(
            (user?.organizations || [])
                .map((o: any) => o.urlSlug)
                .filter((slug: string | null): slug is string => !!slug)
        )),
        [user]
    );

    const [students, setStudents] = useState<RosterRow[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [onlyEnabled, setOnlyEnabled] = useState(false);
    const [enablingId, setEnablingId] = useState<number | null>(null);

    useEffect(() => {
        if (!hasFillApp || slugs.length === 0) return;
        let cancelled = false;
        Promise.allSettled(
            slugs.map((slug: string) =>
                axios.get(`fillapp/tutor/roster/${slug}/`)
                    .then(res => res.data.students as RosterRow[])
            )
        ).then(results => {
            if (cancelled) return;
            const all: RosterRow[] = [];
            const failures: any[] = [];
            results.forEach(r => {
                if (r.status !== 'fulfilled') { failures.push(r.reason); return; }
                // organizationName comes from the row: it is the student's own
                // org, which the slug we asked for cannot tell us.
                r.value.forEach(row => all.push(row));
            });
            setStudents(all);
            // A dropped response used to render as an empty roster, which reads as
            // "your students are gone" rather than "the request failed" — and one
            // request per slug, rather than one per membership, means a single
            // failure can now empty the whole page instead of thinning it.
            //
            // But a 401 here is routine, not a fault: sign-in returns the union of
            // every org's customizations, so this page can be switched on for the
            // tutor while some of their slugs have no College-Apps org behind them,
            // and the roster answers those 401 by design. Reporting it would put a
            // permanent red banner over a perfectly good list. Surface a failure
            // only when it is something other than that, or when every request
            // failed — which means the session or the server really is gone.
            const unexpected = failures.filter(f => f?.response?.status !== 401);
            const everythingFailed = failures.length === results.length;
            setError(unexpected.length > 0 || everythingFailed ? (
                (unexpected[0] ?? failures[0])?.response?.data?.message
                || 'Failed to load College Apps.'
            ) : null);
        });
        return () => { cancelled = true; };
    }, [hasFillApp, slugs]);

    const enableStudent = (row: RosterRow) => {
        if (!isAdmin || enablingId !== null || row.fillApp?.isEnabled) return;
        setEnablingId(row.studentOrganizationId);
        axios.post(`fillapp/tutor/enable/${row.studentOrganizationId}/`)
            .then(res => {
                setStudents(prev => prev.map(r => r.studentOrganizationId === row.studentOrganizationId
                    ? {
                        ...r,
                        fillApp: {
                            id: res.data.studentFillAppId,
                            isEnabled: res.data.isEnabled,
                            cycleYear: res.data.cycleYear,
                            lastSavedAt: r.fillApp?.lastSavedAt || null,
                            sectionStatus: r.fillApp?.sectionStatus || {},
                            progressPercent: r.fillApp?.progressPercent ?? 0,
                        }
                    }
                    : r
                ));
            })
            .catch(err => console.error(err))
            .finally(() => setEnablingId(null));
    };

    const filteredStudents = useMemo(() => students.filter(s => {
        if (onlyEnabled && !s.fillApp?.isEnabled) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return s.studentName.toLowerCase().includes(q)
            || s.studentEmail.toLowerCase().includes(q)
            || s.organizationName.toLowerCase().includes(q);
    }), [students, search, onlyEnabled]);

    const enabledCount = useMemo(
        () => students.filter(s => s.fillApp?.isEnabled).length,
        [students]
    );

    // Actions are always present now: Common App + Essays for any tutor on an
    // enabled student; Enable (admin-only) on a not-enabled one. The empty
    // trailing header keeps the actions column non-sortable.
    const header = ['Name', 'Student Email', 'Organization', 'College Apps', 'Last Activity', ''];
    const columnTypes = ['string', 'string', 'string', 'number', 'date', ''];

    const rows = filteredStudents.map(row => {
        const fa = row.fillApp;
        const enabled = !!fa?.isEnabled;
        const pct = progressPct(fa);
        const pctClass = progressColor(pct);

        // FillApp status: a pill plus inline progress when enabled. Progress only
        // exists once the workspace is enabled, so a not-enabled row shows just the pill.
        const fillAppCell = enabled ? (
            <div className='fillApp__fa'>
                <span className='fillApp__pill fillApp__pill--on'><span className='fillApp__dot' />Enabled</span>
                <span className='fillApp__progressCell'>
                    <span className='fillApp__progressBar'>
                        <span className={`fillApp__progressFill ${pctClass}`} style={{ width: `${pct}%` }} />
                    </span>
                    <span className={`fillApp__pct ${pctClass}`}>{pct}%</span>
                </span>
            </div>
        ) : (
            <div className='fillApp__fa'>
                <span className='fillApp__pill fillApp__pill--off'><span className='fillApp__dot' />Not enabled</span>
            </div>
        );

        // Common App + Essays are both FillApp features, so they appear only when
        // enabled. Enable stays admin-only.
        const actionCell = (
            <div className='fillApp__actions'>
                {enabled ? (
                    <button
                        className='fillApp__reviewBtn'
                        onClick={() => navigate(`/college-apps/review/${row.studentOrganizationId}`)}
                    >Review</button>
                ) : (isAdmin && (
                    <button
                        className='fillApp__enableBtn'
                        disabled={enablingId !== null}
                        onClick={() => enableStudent(row)}
                    >Enable</button>
                ))}
            </div>
        );

        return [
            row.studentName,
            row.studentEmail,
            row.organizationName,
            fillAppCell,
            relativeTime(fa?.lastSavedAt || null),
            actionCell,
        ];
    });

    const sortValues = filteredStudents.map(row => {
        const fa = row.fillApp;
        const enabled = !!fa?.isEnabled;
        return [
            row.studentName,
            row.studentEmail,
            row.organizationName,
            enabled ? progressPct(fa) : null,   // not-enabled sorts below enabled
            fa?.lastSavedAt ? new Date(fa.lastSavedAt).getTime() : 0,
            null,
        ];
    });

    const onSelectCell = filteredStudents.map(row => {
        const navToProfile = () => navigate(`/students/${row.studentOrganizationId}`);
        return [navToProfile, null, null, null, null, null];
    });

    return (
        <div className='fillApp'>
            <Helmet><title>College Apps - Test Center Admin</title></Helmet>
            <Header title='College Apps' />

            {error && <div className='fillApp__error'>{error}</div>}

            <div className='fillApp__bar'>
                <div className='fillApp__barLeft'>
                    <div className='fillApp__search'>
                        <MdSearch className='fillApp__searchIcon' />
                        <input
                            className='fillApp__searchInput'
                            placeholder='search student'
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className='fillApp__tableHeader'>
                <div className='fillApp__select'>
                    <p className={onlyEnabled
                        ? 'fillApp__selected--active'
                        : 'fillApp__selected'
                    } onClick={() => setOnlyEnabled(true)}>
                        Show Enabled ({enabledCount})
                    </p>
                    <p>&nbsp;|&nbsp;</p>
                    <p className={onlyEnabled
                        ? 'fillApp__selected'
                        : 'fillApp__selected--active'
                    } onClick={() => setOnlyEnabled(false)}>
                        Show All ({students.length})
                    </p>
                </div>
            </div>

            <div className='fillApp__tableWrap'>
                <Table
                    header={header}
                    columnTypes={columnTypes}
                    rows={rows}
                    sortValues={sortValues}
                    onSelectCell={onSelectCell}
                />
            </div>
        </div>
    );
}

export default FillApp;
